import {
  useWSModal,
  WSActions,
  WSDivider,
  WSForm,
  WSInfoBox,
  WSInputPassword,
  WSList
} from "@wingspanhq/fe-component-library";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import * as Yup from "yup";
import { useActivateCard } from "../../../../query/cards/mutations/useActivateCard";
import { encryptCardPin } from "../../utils/encryptCardPin";

export interface ActivateCardProps {
  cardId: string;
  onSuccess: () => void;
  onClose: () => void;
}

const validationSchema = Yup.object().shape({
  newPin: Yup.string()
    .required("New PIN is required")
    .matches(/^\d{4}$/, "PIN must be 4 digits"),
  confirmPin: Yup.string()
    .required("Confirm PIN is required")
    .oneOf([Yup.ref("newPin")], "PINs must match")
});

const ActivateCard: React.FC<ActivateCardProps> = ({
  cardId,
  onClose,
  onSuccess
}) => {
  const [activateCard, activateCardMeta] = useActivateCard();

  return (
    <WSList gap="L">
      <WSInfoBox>Create a 4-digit PIN</WSInfoBox>
      <WSForm
        defaultValues={{
          newPin: "",
          confirmPin: ""
        }}
        validationSchema={validationSchema}
        onSubmit={async ({ confirmPin }) => {
          const encryptedPin = await encryptCardPin(confirmPin);

          await activateCard(
            {
              cardId,
              pin: encryptedPin
            },
            {
              onSuccess: () => {
                onSuccess();
                onClose();
              }
            }
          );
        }}
      >
        <WSList gap="L">
          <WSForm.Field
            name="newPin"
            label="Create PIN"
            component={WSInputPassword}
            componentProps={{
              required: true,
              maxLength: 4
            }}
          />
          <WSForm.Field
            name="confirmPin"
            label="Re-enter PIN"
            component={WSInputPassword}
            componentProps={{
              required: true,
              maxLength: 4
            }}
          />
          <WSErrorMessage contextKey="Other" error={activateCardMeta.error} />
          <WSDivider />
          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Continue",
                type: "submit",
                kind: "Primary"
              },
              {
                label: "Cancel",
                onClick: onClose,
                kind: "Secondary"
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSList>
  );
};

export function useActivateCardModal() {
  return useWSModal(ActivateCard, {
    title: "Activate physical card",
    size: "S"
  });
}
